import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import {
  PrismBanner,
  PrismComboSelectInput,
  PrismViewSwitcher,
} from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import type { Dayjs } from 'dayjs';
import { t } from 'ember-intl';
import { modifier } from 'ember-modifier';
import type { Hotelinfo } from 'frontend-utils/models';
import { REVERSE_ISO_DAYS } from 'frontend-utils/utils/constants/constant-values';
import SettingsRecommendationsCompetitorsImpactDataTableRow from 'lumen/components/settings/recommendations/competitors-impact/data-table-row';
import SettingsRecommendationsCompetitorsImpactDragHandle from 'lumen/components/settings/recommendations/competitors-impact/drag-handle';
import type { RecommendationStrategy } from 'lumen/models';
import type {
  CompsetPositioningLevelValues,
  CompsetPositioningSettings,
} from 'lumen/models/recommendation-strategy';
import {
  COMPETITOR_IMPACT_STRATEGY_INIT_EMPTY,
  COMPETITORS_IMPACTS_STRATEGY_DAY_OF_WEEK_ADJUSTMENT_OPTIONS,
  COMPETITORS_IMPACTS_STRATEGY_LEVELS,
} from 'lumen/utils/constants/pricing-assistant-constants';
import { updateCompetitorImpactForSpecifiedDays } from 'lumen/utils/pricing-assistant/recommendation-settings';

type DayOfWeekAdjustmentDropdownOption = Option & {
  id: (typeof COMPETITORS_IMPACTS_STRATEGY_DAY_OF_WEEK_ADJUSTMENT_OPTIONS)[keyof typeof COMPETITORS_IMPACTS_STRATEGY_DAY_OF_WEEK_ADJUSTMENT_OPTIONS];
};

interface DayOfWeekViewSwitcherButton {
  disabled: boolean;
  id: string;
  label: string;
}

export type FormattedCompetitorsType = {
  id: string;
  name: string;
};

interface SettingsRecommendationsCompetitorsImpactPickerSignature {
  Args: {
    componentSeasonEndDt: Dayjs | null;
    componentSeasonStartDt: Dayjs | null;
    isEditMode: boolean;
    saveAttempted: boolean;
    strategy: RecommendationStrategy;
  };
  Element: HTMLDetailsElement;
}

export default class SettingsRecommendationsCompetitorsImpactPicker extends Component<SettingsRecommendationsCompetitorsImpactPickerSignature> {
  @service declare alerting: Services['alerting'];
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked selectedDowAdjustmentOption?: DayOfWeekAdjustmentDropdownOption;
  // eslint-disable-next-line ember/no-tracked-properties-from-args
  @tracked strategy = this.args.strategy;
  @tracked userSelectedDayOfWeekViewSwitcherActiveButtonId?: string;

  previousEditMode?: boolean;

  // Default selected Dow adjustment option - 'Any Day' if all days of week have the same settings
  setDefaultDowAdjustmentOption = modifier(
    (element, [isEditMode]: [boolean]): void => {
      // The modifier runs everytime isEditMode changes and also when competitor is dragged between rows
      // However, we want the settings of default Dow adjustment option to only happen when isEditMode changes (emulating did-update render modifer behaviour)
      if (this.previousEditMode !== isEditMode && isEditMode) {
        // If there is only one group, then it means that all days of week have the same settings
        if (this.groupedDayofWeek.length === 1) {
          this.selectedDowAdjustmentOption = this.dowAdjustmentOptions.find(
            (option) =>
              option.id ===
              COMPETITORS_IMPACTS_STRATEGY_DAY_OF_WEEK_ADJUSTMENT_OPTIONS.ANY_DAY,
          );
        } else {
          this.selectedDowAdjustmentOption = this.dowAdjustmentOptions.find(
            (option) =>
              option.id ===
              COMPETITORS_IMPACTS_STRATEGY_DAY_OF_WEEK_ADJUSTMENT_OPTIONS.PER_DAY,
          );
        }
      }

      this.previousEditMode = isEditMode;
    },
  );

  get competitors(): {
    formattedCompetitors: Record<string, FormattedCompetitorsType>;
    formattedCompetitorsCompetitorsByLevel: Record<
      CompsetPositioningLevelValues,
      FormattedCompetitorsType[]
    >;
  } {
    const competitorsByLevel: Record<
      CompsetPositioningLevelValues,
      FormattedCompetitorsType[]
    > = {
      [COMPETITORS_IMPACTS_STRATEGY_LEVELS.MUCH_MORE_EXPENSIVE]: [],
      [COMPETITORS_IMPACTS_STRATEGY_LEVELS.MORE_EXPENSIVE]: [],
      [COMPETITORS_IMPACTS_STRATEGY_LEVELS.SIMILAR]: [],
      [COMPETITORS_IMPACTS_STRATEGY_LEVELS.CHEAPER]: [],
      [COMPETITORS_IMPACTS_STRATEGY_LEVELS.MUCH_CHEAPER]: [],
      [COMPETITORS_IMPACTS_STRATEGY_LEVELS.IRRELEVANT]: [],
      [COMPETITOR_IMPACT_STRATEGY_INIT_EMPTY]: [],
    };

    const formattedCompetitors: Record<string, FormattedCompetitorsType> = {};

    const settings = this.compsetSettings;
    const firstImpactedDay = this.impactedDays[0];

    if (!settings || !firstImpactedDay) {
      return {
        formattedCompetitorsCompetitorsByLevel: competitorsByLevel,
        formattedCompetitors,
      };
    }

    const daySettingsObject = settings.day_of_week[firstImpactedDay];

    if (!daySettingsObject) {
      return {
        formattedCompetitorsCompetitorsByLevel: competitorsByLevel,
        formattedCompetitors,
      };
    }

    Object.entries(daySettingsObject).forEach(([competitorId, level]) => {
      if (!competitorsByLevel[level]) {
        competitorsByLevel[level] = [];
      }

      const record = this.store.peekRecord<Hotelinfo>(
        'hotelinfo',
        competitorId,
      );
      const formattedCompetitor = {
        id: competitorId,
        name: record?.name ?? '',
      };

      formattedCompetitors[competitorId] = formattedCompetitor;
      competitorsByLevel[level].push(formattedCompetitor);
    });

    return {
      formattedCompetitorsCompetitorsByLevel: competitorsByLevel,
      formattedCompetitors,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get competitorsImpactStrategyLevels() {
    return Object.values(COMPETITORS_IMPACTS_STRATEGY_LEVELS);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get competitorsLeftToAssignDuringInitialAssignment() {
    return this.args.strategy
      .compsetPositioningCompetitorsLeftToAssignInStartingState;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get compsetSettings() {
    const { settings } = this.strategy;
    return settings.settings_type === 'compset_positioning' ? settings : null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dayOfWeekViewSwitcherActiveButtonId() {
    const enabledDayOfWeekViewSwitcherButtons =
      this.dayOfWeekViewSwitcherButtons.filter((button) => !button.disabled);

    if (this.userSelectedDayOfWeekViewSwitcherActiveButtonId) {
      // Case 1: If user has selected a day/group and it's present in the current non-disabled view switcher buttons, select the selected day/group
      const isSelectedDayPresent = enabledDayOfWeekViewSwitcherButtons.find(
        (day) => {
          return (
            day.id === this.userSelectedDayOfWeekViewSwitcherActiveButtonId
          );
        },
      );

      if (isSelectedDayPresent) {
        return this.userSelectedDayOfWeekViewSwitcherActiveButtonId;
      }

      // Case 2: If user has selected a day/group, but it's not present in the non-disabled current view switcher buttons, check if the first selected day/group is present in the current view switcher buttons
      const selectedViewSwitcherButtonIds =
        this.userSelectedDayOfWeekViewSwitcherActiveButtonId
          .split(',')
          .map((id) => id.trim());
      const firstSelectedNonDisabledViewSwitcherButton =
        selectedViewSwitcherButtonIds.find((day) => {
          return enabledDayOfWeekViewSwitcherButtons.find(
            (button) => button.id === day,
          );
        });

      if (firstSelectedNonDisabledViewSwitcherButton) {
        return (
          this.dayOfWeekViewSwitcherButtons.find((day) => {
            return day.id === firstSelectedNonDisabledViewSwitcherButton;
          }) as DayOfWeekViewSwitcherButton
        ).id;
      }
    }

    // Case 3: If none of the above conditions are met, select the first enabled view switcher button
    return (
      enabledDayOfWeekViewSwitcherButtons[0] as DayOfWeekViewSwitcherButton
    ).id;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dayOfWeekViewSwitcherButtonIdsWithinStrategyStartAndEnd() {
    // Note: We only update date range on strategy when it is saved, however, we want the disabled buttons to be updated right when the date range is changed
    let startDate = this.args.componentSeasonStartDt;
    const endDate = this.args.componentSeasonEndDt;

    if (startDate && endDate && Math.abs(startDate.diff(endDate, 'days')) < 7) {
      const daysInBetween = [];

      while (startDate.isSameOrBefore(endDate)) {
        daysInBetween.push(startDate.isoWeekday().toString());
        startDate = startDate.add(1, 'days');
      }
      return daysInBetween;
    }

    return ['1', '2', '3', '4', '5', '6', '7'];
  }

  @cached
  get dayOfWeekViewSwitcherButtons(): DayOfWeekViewSwitcherButton[] {
    let groups;

    // Edit mode: version based on DOW dropdown
    if (this.args.isEditMode) {
      if (
        this.selectedDowAdjustmentOption?.id ===
        COMPETITORS_IMPACTS_STRATEGY_DAY_OF_WEEK_ADJUSTMENT_OPTIONS.ANY_DAY
      ) {
        groups = [['1', '2', '3', '4', '5', '6', '7']];
      } else {
        groups = [['1'], ['2'], ['3'], ['4'], ['5'], ['6'], ['7']];
      }
    } else {
      // Non-edit mode: Grouped version
      groups = this.groupedDayofWeek;
    }

    return this.formatDayOfWeekGroupsToViewSwitcherButtons(groups);
  }

  @cached
  get dowAdjustmentOptions(): DayOfWeekAdjustmentDropdownOption[] {
    return [
      {
        id: COMPETITORS_IMPACTS_STRATEGY_DAY_OF_WEEK_ADJUSTMENT_OPTIONS.ANY_DAY,
        label: this.intl.t(
          'pricing_assistant.recommendations.competitors_impacts.days_of_week_dropdown.anyday_title',
        ),
        unsafe_subLabel: this.intl.t(
          'pricing_assistant.recommendations.competitors_impacts.days_of_week_dropdown.anyday_subtitle',
        ),
        icon: 'view-calendar',
      },
      {
        id: COMPETITORS_IMPACTS_STRATEGY_DAY_OF_WEEK_ADJUSTMENT_OPTIONS.PER_DAY,
        label: this.intl.t(
          'pricing_assistant.recommendations.competitors_impacts.days_of_week_dropdown.specific_day_title',
        ),
        unsafe_subLabel: this.intl.t(
          'pricing_assistant.recommendations.competitors_impacts.days_of_week_dropdown.specific_day_subtitle',
        ),
        icon: 'calendar-default',
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedCompetitorsLeftToAssign() {
    return this.competitorsLeftToAssignDuringInitialAssignment.map(
      (competitorId) => {
        return this.competitors.formattedCompetitors[
          competitorId
        ] as FormattedCompetitorsType;
      },
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get groupedDayofWeek() {
    const settings = this.compsetSettings;
    return this.groupDayOfWeek(settings?.day_of_week ?? {});
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get impactedDays() {
    return this.dayOfWeekViewSwitcherActiveButtonId.split(',').map(Number);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isUserDefiningInitialImpact() {
    return this.args.strategy.isCompsetPositioningStartingState;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showAllCompetitorsMappedBanner() {
    return (
      this.competitorsLeftToAssignDuringInitialAssignment.length === 0 &&
      !this.args.strategy.doesCompsetPositioningHaveInvalidSelection
        .hasInvalidSelection
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showDaysOfWeekInputs() {
    return this.competitorsLeftToAssignDuringInitialAssignment.length === 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showNoRelevantCompetitorsBanner() {
    return (
      this.args.saveAttempted &&
      this.args.strategy.doesCompsetPositioningHaveInvalidSelection
        .hasInvalidSelection
    );
  }

  /**
   * Formats groups of days of the week to the desired input required by a view switcher component.
   * @param {Array} groups - An array of arrays, where each sub-array contains the days of the week with the same settings.
   * @returns {Array} An array of objects representing the tabs for the view switcher component, with each object having a `label` and `id` property.
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formatDayOfWeekGroupsToViewSwitcherButtons(groups: string[][]) {
    const tabs: { disabled: boolean; id: string; label: string }[] = [];
    groups.forEach((group) => {
      const label = this.formatViewSwitcherButtonLabels(group);

      const id = group.join(', ');

      const groupHasInvalidSelection = group.some((day) =>
        this.args.strategy.doesCompsetPositioningHaveInvalidSelection.daysWithInvalidSelection.includes(
          day,
        ),
      );

      // Show days outside strategy start and end as disabled in the "per day" edit mode
      const isDayOutsideStrategyStartAndEnd =
        this.strategy.isSeasonalStrategy &&
        this.args.isEditMode &&
        this.selectedDowAdjustmentOption?.id ===
          COMPETITORS_IMPACTS_STRATEGY_DAY_OF_WEEK_ADJUSTMENT_OPTIONS.PER_DAY &&
        !this.dayOfWeekViewSwitcherButtonIdsWithinStrategyStartAndEnd.includes(
          group[0] as string,
        );

      // Disable the view switcher buttons that don't have invalid selections
      const disabled =
        (this.args.strategy.doesCompsetPositioningHaveInvalidSelection
          .hasInvalidSelection &&
          !groupHasInvalidSelection) ||
        isDayOutsideStrategyStartAndEnd;

      tabs.push({ label, id, disabled });
    });

    return tabs;
  }

  /**
   * Formats the days of the week group to a human-readable format.
   * @param {Array} group - An array of days of the week.
   * @returns {String} A human-readable string representing the days of the week.
   * @example
   * formatViewSwitcherButtonLabels(['1', '2', '3', '4', '5', '6', '7']); // 'Mon-Sun'
   * formatViewSwitcherButtonLabels(['1', '2', '4', '5', '6', '7']); // 'Mon-Tue, Thu-Sun'
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formatViewSwitcherButtonLabels(group: string[]) {
    const result = [];

    for (let i = 0; i < group.length; i++) {
      const start = Number(group[i]);
      let end = Number(group[i]);

      while (Number(group[i + 1]) - Number(group[i]) === 1) {
        end = Number(group[i + 1]);
        i += 1;
      }

      const startIsoWeekday = REVERSE_ISO_DAYS[start];
      const endIsoWeekday = REVERSE_ISO_DAYS[end];

      if (start === end && startIsoWeekday) {
        result.push(this.intl.t(`days_short.${startIsoWeekday}`));
      } else if (startIsoWeekday && endIsoWeekday) {
        result.push(
          `${this.intl.t(`days_short.${startIsoWeekday}`)}-${this.intl.t(`days_short.${endIsoWeekday}`)}`,
        );
      }
    }

    return result.join(', ');
  }

  /**
   * Groups days of the week with the same setting configuration.
   * @param {Object} daysOfWeek - An object representing the days of the week and their settings.
   * @returns {Array} An array of arrays, where each sub-array contains the days of the week with the same settings.
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  groupDayOfWeek(daysOfWeek: CompsetPositioningSettings['day_of_week']) {
    const groups: Record<string, string[]> = {};

    Object.entries(daysOfWeek).forEach(([day, value]) => {
      const sortedValues = Object.fromEntries(Object.entries(value).sort());
      const valuesString = JSON.stringify(sortedValues); // stringify to compare objects

      if (!groups[valuesString]) {
        groups[valuesString] = [];
      }

      groups[valuesString].push(day);
    });

    return Object.values(groups);
  }

  @action onCompetitorImpactUpdate(
    competitorId: string,
    newLevel: CompsetPositioningLevelValues,
  ): void {
    updateCompetitorImpactForSpecifiedDays(
      this.args.strategy,
      [competitorId],
      newLevel,
      this.impactedDays,
    );
  }

  @action updateSelectedDay(dayId: string): void {
    this.userSelectedDayOfWeekViewSwitcherActiveButtonId = dayId;
  }

  @action
  async updateSelectedDowAdjustmentOption(
    option: DayOfWeekAdjustmentDropdownOption,
  ): Promise<void> {
    // If there is more than 1 group, it means that not all days of the week have the same settings. In this case we'll show the warning alert first. Otherwise we skip it.
    if (
      option.id ===
        COMPETITORS_IMPACTS_STRATEGY_DAY_OF_WEEK_ADJUSTMENT_OPTIONS.ANY_DAY &&
      this.groupedDayofWeek.length !== 1
    ) {
      const confirmedChangeToAnyDay = await this.alerting.showWarningAlert({
        title: this.intl.t(
          'pricing_assistant.recommendations.competitors_impacts.days_of_week_warning.title',
        ),
        text: this.intl.t(
          'pricing_assistant.recommendations.competitors_impacts.days_of_week_warning.description',
        ),
        buttons: {
          cancel: {
            text: this.intl.t('general.cancel'),
          },
          confirm: {
            text: this.intl.t('general.confirm'),
          },
        },
      });

      if (confirmedChangeToAnyDay) {
        this.selectedDowAdjustmentOption = option;

        const settings = this.strategy.settings;
        if (settings.settings_type !== 'compset_positioning') {
          return;
        }

        // For now, we switch to monday settings configuration when user switches to any day
        const mondaySettings = settings.day_of_week[1];

        const newDayOfWeekSettings: CompsetPositioningSettings['day_of_week'] =
          {};
        if (mondaySettings) {
          for (let day = 1; day <= 7; day++) {
            newDayOfWeekSettings[day] = mondaySettings;
          }

          this.strategy.settings = {
            ...settings,
            day_of_week: newDayOfWeekSettings,
          };
        }
      }
    } else {
      this.selectedDowAdjustmentOption = option;
    }

    if (
      option.id ===
      COMPETITORS_IMPACTS_STRATEGY_DAY_OF_WEEK_ADJUSTMENT_OPTIONS.PER_DAY
    ) {
      this.usageAnalytics.track("'Per DOW' Competitor impact selected", {
        eventTrigger: 'recommendation-settings',
        product: 'pricing-manager',
      });
    }
  }

  <template>
    <div
      class="flex flex-col gap-400"
      {{this.setDefaultDowAdjustmentOption @isEditMode}}
    >
      {{#if this.isUserDefiningInitialImpact}}
        {{#if this.competitorsLeftToAssignDuringInitialAssignment.length}}
          <div class="explanation-block flex flex-col gap-100 p-400">
            <h4>{{t
                "pricing_assistant.recommendations.competitors_impacts.explanation.title"
              }}</h4>
            <p class="text-300--regular">{{t
                "pricing_assistant.recommendations.competitors_impacts.explanation.description"
              }}</p>
          </div>
          <div class="flex flex-col">
            <div class="flex flex-row flex-wrap gap-400 p-400">
              {{#each this.formattedCompetitorsLeftToAssign as |competitor|}}
                <SettingsRecommendationsCompetitorsImpactDragHandle
                  @id={{competitor.id}}
                  @isEditMode={{@isEditMode}}
                  @title={{competitor.name}}
                />
              {{/each}}
            </div>
            <p class="text-300--regular self-end">
              {{t
                "pricing_assistant.recommendations.competitors_impacts.competitors_left_to_drag"
                class="text-300--bold"
                competitors=this.competitorsLeftToAssignDuringInitialAssignment.length
                htmlSafe=true
              }}</p>
          </div>
        {{/if}}

        {{#if this.showAllCompetitorsMappedBanner}}
          <PrismBanner
            @bannerMessage={{t
              "pricing_assistant.recommendations.competitors_impacts.success_banner_text"
            }}
            @bannerStyle="success"
            @icon="circle-checkmark"
          />
        {{/if}}
      {{/if}}

      {{#if this.showNoRelevantCompetitorsBanner}}
        <PrismBanner
          @bannerMessage={{t
            "pricing_assistant.recommendations.competitors_impacts.no_relevant_competitors_banner"
          }}
          @bannerStyle="critical"
          @icon="warning"
        />
      {{/if}}

      <div class="flex flex-col gap-200">
        {{#if this.showDaysOfWeekInputs}}
          <div class="days-of-week-container grid items-center">
            <div class="col-start-2 justify-self-center">
              {{#if this.dayOfWeekViewSwitcherActiveButtonId}}
                <PrismViewSwitcher
                  @activeButtonId={{this.dayOfWeekViewSwitcherActiveButtonId}}
                  @onSetActiveButtonId={{this.updateSelectedDay}}
                  as |ViewSwitcher|
                >
                  {{#each this.dayOfWeekViewSwitcherButtons as |day|}}
                    <ViewSwitcher.button
                      @id={{day.id}}
                      @isDisabled={{day.disabled}}
                      @label={{day.label}}
                    />
                  {{/each}}
                </PrismViewSwitcher>
              {{/if}}
            </div>
            {{#if @isEditMode}}
              <div class="col-start-3 justify-self-end">
                <PrismComboSelectInput
                  @horizontalPosition="left"
                  @onChange={{this.updateSelectedDowAdjustmentOption}}
                  @options={{this.dowAdjustmentOptions}}
                  @unsafe_dropdownClass="recommendation-settings-dow-adjustment-dropdown"
                  @value={{this.selectedDowAdjustmentOption}}
                />
              </div>
            {{/if}}
          </div>
        {{/if}}
        <div class="data-table flex flex-col">
          {{#each this.competitorsImpactStrategyLevels as |level|}}
            <SettingsRecommendationsCompetitorsImpactDataTableRow
              @competitorLevel={{level}}
              @formattedCompetitors={{this.competitors.formattedCompetitors}}
              @formattedCompetitorsCompetitorsByLevel={{this.competitors.formattedCompetitorsCompetitorsByLevel}}
              @isEditMode={{@isEditMode}}
              @onCompetitorImpactUpdate={{this.onCompetitorImpactUpdate}}
              @strategy={{@strategy}}
            />
          {{/each}}
        </div>
      </div>
    </div>
  </template>
}
